import { useEffect, useRef, useState } from 'react';

/**
 * Renders one page of a PDF to a canvas, at a chosen zoom.
 *
 * pdf.js rather than an <iframe>, deliberately, and the reason is precision rather than
 * preference. The placement tool must convert a click into an exact PDF coordinate. An
 * iframe hosts the browser's own PDF viewer, which brings its own toolbar, its own
 * scrolling and its own fit behaviour — so an overlay aligned to it is aligned to a
 * guess. A canvas the page is drawn onto IS the page: one known scale factor, and the
 * conversion becomes arithmetic rather than estimation.
 *
 * pdf.js is loaded by dynamic import so its ~350KB lands only on this screen, not in the
 * bundle every operator downloads to look at a queue.
 */
export interface RenderedPage {
  canvas: HTMLCanvasElement | null;
  /** The page's true size in PDF points — the unit every stored coordinate is in. */
  pointWidth: number;
  pointHeight: number;
  /** Rendered CSS pixels per PDF point. The whole conversion rests on this. */
  scale: number;
  pageCount: number;
  loading: boolean;
  error: string | null;
}

export function usePdfPage(url: string | null, pageNumber: number, targetWidth: number): RenderedPage {
  const [state, setState] = useState<RenderedPage>({
    canvas: null,
    pointWidth: 595.28,
    pointHeight: 841.89,
    scale: 1,
    pageCount: 0,
    loading: Boolean(url),
    error: null,
  });
  // Guards against a slow render for page 1 landing after the user has moved to page 2.
  const renderToken = useRef(0);

  useEffect(() => {
    if (!url) {
      setState((s) => ({ ...s, loading: false, canvas: null }));
      return;
    }

    const token = ++renderToken.current;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        // The worker is bundled rather than fetched from a CDN: this console may run
        // inside a network that cannot reach one, and a silent worker failure makes the
        // tool look broken rather than offline.
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;

        const doc = await pdfjs.getDocument({ url, isEvalSupported: false }).promise;
        if (cancelled || token !== renderToken.current) return;

        const page = await doc.getPage(Math.min(Math.max(pageNumber, 1), doc.numPages));
        const unscaled = page.getViewport({ scale: 1 });
        const scale = targetWidth / unscaled.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        // Rendered at device resolution so the page is crisp on a retina display, while
        // the CSS size stays at the layout width — otherwise the click-to-point maths is
        // out by the device pixel ratio.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const context = canvas.getContext('2d');
        if (!context) throw new Error('could not get a 2D context');
        context.scale(dpr, dpr);
        await page.render({ canvasContext: context, viewport }).promise;

        if (cancelled || token !== renderToken.current) return;
        setState({
          canvas,
          pointWidth: unscaled.width,
          pointHeight: unscaled.height,
          scale,
          pageCount: doc.numPages,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error:
            error instanceof Error
              ? `Could not render the template: ${error.message}`
              : 'Could not render the template.',
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, pageNumber, targetWidth]);

  return state;
}
